import React, { useState, useEffect, useCallback } from "react";
import { projectAPI } from "../../services/projectAPI";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  ButtonGroup,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  SimpleGrid,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Progress,
  Heading,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast
} from "@chakra-ui/react";
import {
  FiSearch,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiFilter,
  FiMoreVertical,
  FiCalendar,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiFolder,
 FiFolder as FiBarChart3,
  FiClock,
  FiList
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useProject } from "../../contexts/ProjectContext";
import { usePermission } from "../../hooks/usePermission";

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { projects, loading, error, fetchProjects, deleteProject } = useProject();
  const toast = useToast();

  const {
    canCreateProjects,
    canEditProjects,
    canDeleteProjects,
    canViewProjects,
  } = usePermission();

  // Local state for filters
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    projectType: "",
    page: 0,
    size: 10,
  });

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // ✅ CHANGED: Default to table view
  
  // ✅ Add debounced search to prevent too many API calls
  const [searchDebounce, setSearchDebounce] = useState(null);

  // ✅ FIXED: Safer fetch function with error handling
  const fetchProjectsWithFilters = useCallback(async () => {
    if (!canViewProjects()) return;

    try {
      // Build clean params object, removing empty values
      const cleanFilters = Object.keys(filters).reduce((acc, key) => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
          acc[key] = filters[key];
        }
        return acc;
      }, {});

      await fetchProjects(cleanFilters);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast({
        title: "Error Loading Projects",
        description: "Failed to load projects. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [fetchProjects, filters, canViewProjects, toast]);

  // ✅ Fetch projects when component mounts or filters change
  useEffect(() => {
    fetchProjectsWithFilters();
  }, [fetchProjectsWithFilters]);

  // ✅ Separate effect for stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!canViewProjects()) return;

      try {
        setStatsLoading(true);
        const response = await projectAPI.getProjectStats();

        if (response.data.success) {
          setStats(response.data.data);
        } else {
          throw new Error("Failed to fetch stats");
        }
      } catch (error) {
        console.error("Failed to load dashboard stats:", error);
        // Don't show error toast for stats, just log it
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [canViewProjects]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 0,
    }));
  }, []);

  // ✅ FIXED: Debounced search to prevent API spam
  const handleSearch = useCallback((searchValue) => {
    // Clear previous timeout
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    // Set new timeout
    const newTimeout = setTimeout(() => {
      handleFilterChange("search", searchValue);
    }, 500); // Wait 500ms after user stops typing

    setSearchDebounce(newTimeout);
  }, [handleFilterChange, searchDebounce]);

  const handleDeleteProject = async (projectId) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await deleteProject(projectId);
        fetchProjectsWithFilters();
        toast({
          title: "Project Deleted",
          description: "Project has been successfully deleted.",
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        console.error("Failed to delete project:", error);
        toast({
          title: "Delete Failed",
          description: "Failed to delete project. Please try again.",
          status: "error",
          duration: 4000,
        });
      }
    }
  };

  // Permission check
  if (!canViewProjects()) {
    return (
      <Box p={8} textAlign="center">
        <Card bg="gray.800" borderColor="orange.500" borderWidth="1px">
          <CardBody>
            <VStack spacing={4}>
              <Icon as={FiFolder} boxSize={12} color="orange.400" />
              <VStack spacing={2}>
                <Heading size="md" color="white">Access Denied</Heading>
                <Text color="gray.400">You don't have permission to view projects.</Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      active: "green",
      pending: "yellow", 
      completed: "blue",
      cancelled: "red",
      planning: "purple"
    };
    return colors[status?.toLowerCase()] || "gray";
  };

  const getProjectProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'active') return Math.floor(Math.random() * 80) + 20;
    if (project.status === 'pending') return Math.floor(Math.random() * 20);
    return 0;
  };

  // Enhanced Stats Cards Component
  const StatsCards = () => (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
      {/* <Card bg="gray.800" borderColor="blue.500" borderWidth="1px" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
        <CardBody>
          <Stat>
            <HStack justify="space-between">
              <VStack align="start" spacing={2}>
                <StatLabel color="gray.400" fontSize="sm">Total Projects</StatLabel>
                <StatNumber color="white" fontSize="2xl">{stats?.totalProjects || 0}</StatNumber>
                <StatHelpText color="blue.400" fontSize="xs" m={0}>
                  <Icon as={FiFolder} mr={1} />
                  All time
                </StatHelpText>
              </VStack>
              <Box p={3} bg="blue.500" borderRadius="lg">
                <Icon as={FiBarChart3} color="white" boxSize={6} />
              </Box>
            </HStack>
          </Stat>
        </CardBody>
      </Card>

      <Card bg="gray.800" borderColor="green.500" borderWidth="1px" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
        <CardBody>
          <Stat>
            <HStack justify="space-between">
              <VStack align="start" spacing={2}>
                <StatLabel color="gray.400" fontSize="sm">Active Projects</StatLabel>
                <StatNumber color="white" fontSize="2xl">{stats?.activeProjects || 0}</StatNumber>
                <StatHelpText color="green.400" fontSize="xs" m={0}>
                  <Icon as={FiTrendingUp} mr={1} />
                  In progress
                </StatHelpText>
              </VStack>
              <Box p={3} bg="green.500" borderRadius="lg">
                <Icon as={FiClock} color="white" boxSize={6} />
              </Box>
            </HStack>
          </Stat>
        </CardBody>
      </Card>

      <Card bg="gray.800" borderColor="purple.500" borderWidth="1px" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
        <CardBody>
          <Stat>
            <HStack justify="space-between">
              <VStack align="start" spacing={2}>
                <StatLabel color="gray.400" fontSize="sm">Completed</StatLabel>
                <StatNumber color="white" fontSize="2xl">{stats?.completedProjects || 0}</StatNumber>
                <StatHelpText color="purple.400" fontSize="xs" m={0}>
                  <Icon as={FiUsers} mr={1} />
                  Finished
                </StatHelpText>
              </VStack>
              <Box p={3} bg="purple.500" borderRadius="lg">
                <Icon as={FiUsers} color="white" boxSize={6} />
              </Box>
            </HStack>
          </Stat>
        </CardBody>
      </Card>

      <Card bg="gray.800" borderColor="orange.500" borderWidth="1px" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
        <CardBody>
          <Stat>
            <HStack justify="space-between">
              <VStack align="start" spacing={2}>
                <StatLabel color="gray.400" fontSize="sm">Success Rate</StatLabel>
                <StatNumber color="white" fontSize="2xl">
                  {stats?.totalProjects ? Math.round((stats.completedProjects / stats.totalProjects) * 100) : 0}%
                </StatNumber>
                <StatHelpText color="orange.400" fontSize="xs" m={0}>
                  <Icon as={FiDollarSign} mr={1} />
                  Completion rate
                </StatHelpText>
              </VStack>
              <Box p={3} bg="orange.500" borderRadius="lg">
                <Icon as={FiDollarSign} color="white" boxSize={6} />
              </Box>
            </HStack>
          </Stat>
        </CardBody>
      </Card> */}
    </SimpleGrid>
  );

  // Project Grid Card Component  
  const ProjectCard = ({ project }) => {
    const progress = getProjectProgress(project);
    
    return (
      <Card 
        bg="gray.800" 
        borderColor="gray.700" 
        borderWidth="1px"
        _hover={{ 
          transform: 'translateY(-4px)', 
          shadow: '2xl',
          borderColor: 'blue.500'
        }} 
        transition="all 0.3s"
        cursor="pointer"
        onClick={() => navigate(`/dashboard/company/projects/${project.id}`)}
      >
        <CardBody p={6}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="start">
              <VStack align="start" spacing={1} flex={1}>
                <Text fontSize="lg" fontWeight="bold" color="white" noOfLines={1}>
                  {project.name}
                </Text>
                <Text fontSize="sm" color="gray.400" noOfLines={2}>
                  {project.description}
                </Text>
              </VStack>
              <Menu>
                <MenuButton 
                  as={IconButton}
                  icon={<FiMoreVertical />}
                  variant="ghost"
                  size="sm"
                  color="gray.400"
                  _hover={{ color: 'white', bg: 'gray' }}
                  onClick={(e) => e.stopPropagation()} tyle={{backgroundColor: 'black'}}
                />
                <MenuList bg="gray.800" borderColor="gray.600">
                  <MenuItem 
                    icon={<FiEye />} 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/company/projects/${project.id}`);
                    }}
                    _hover={{ bg: 'gray.700' }}
                    color="gray.200" style={{backgroundColor: 'gray'}}
                  >
                    View Details
                  </MenuItem>
                  {canEditProjects() && (
                    <MenuItem 
                      icon={<FiEdit3 />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/company/projects/${project.id}/edit`);
                      }}
                      _hover={{ bg: 'gray.700' }} style={{backgroundColor: 'gray'}}
                      color="gray.200"
                    >
                      Edit Project
                    </MenuItem>
                  )}
                  {canDeleteProjects() && (
                    <MenuItem 
                      icon={<FiTrash2 />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project.id);
                      }}
                      _hover={{ bg: 'red.600' }} style={{backgroundColor: 'gray'}}
                      color="red.400"
                    >
                      Delete Project
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </HStack>

            <HStack justify="space-between">
              <Badge
                colorScheme={getStatusColor(project.status)}
                variant="solid"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                textTransform="capitalize"
              >
                {project.status}
              </Badge>
              <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                {project.projectType}
              </Text>
            </HStack>

            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.400">Progress</Text>
                <Text fontSize="xs" color="gray.300" fontWeight="semibold">{progress}%</Text>
              </HStack>
              <Progress 
                value={progress} 
                colorScheme={getStatusColor(project.status)}
                size="sm"
                bg="gray.700"
                borderRadius="full"
              />
            </VStack>

            <HStack justify="space-between" pt={2} borderTop="1px solid" borderColor="gray.700">
              <HStack spacing={1}>
                <Icon as={FiCalendar} boxSize={3} color="gray.500" />
                <Text fontSize="xs" color="gray.500">
                  {new Date(project.createdAt).toLocaleDateString()}
                </Text>
              </HStack>
              <Avatar size="xs" name={project.createdBy || "Unknown"} />
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box p={8} minH="100vh" bg="gray.900">
      <VStack spacing={8} align="stretch">
        {/* Enhanced Header */}
        <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
          <CardBody>
            <HStack justify="space-between" align="start">
              <VStack align="start" spacing={2}>
                <HStack spacing={3}>
                  <Box p={2} bg="blue.500" borderRadius="lg">
                    <Icon as={FiFolder} color="white" boxSize={5} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Heading size="lg" color="white">Project Management</Heading>
                    <Text color="gray.400" fontSize="md">
                      Manage and track your construction projects
                    </Text>
                  </VStack>
                </HStack>
              </VStack>

              <HStack spacing={3}>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    leftIcon={<FiBarChart3 />}
                    colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                    onClick={() => setViewMode('grid')}
                  >
                    Grid
                  </Button>
                  <Button
                    leftIcon={<FiList />}
                    colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                    onClick={() => setViewMode('table')}
                  >
                    Table
                  </Button>
                </ButtonGroup>

                {canCreateProjects() && (
                  <Button
                    leftIcon={<FiPlus />}
                    colorScheme="blue"
                    onClick={() => navigate("/dashboard/company/projects/new")}
                  >
                    New Project
                  </Button>
                )}
              </HStack>
            </HStack>
          </CardBody>
        </Card>

        {/* Enhanced Stats */}
        {statsLoading ? (
          <Card bg="gray.800">
            <CardBody>
              <Flex justify="center" p={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" color="blue.400" thickness="4px" />
                  <Text color="gray.400">Loading dashboard statistics...</Text>
                </VStack>
              </Flex>
            </CardBody>
          </Card>
        ) : (
          stats && <StatsCards />
        )}

        {/* ✅ FIXED: Enhanced Filters with better search handling */}
        {/* <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.400" fontWeight="medium">Filter Projects</Text>
              <HStack spacing={4} wrap="wrap">
                <InputGroup maxW="400px" flex={1}>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search projects by name or description..."
                    defaultValue={filters.search} // ✅ CHANGED: Use defaultValue to prevent controlled component issues
                    onChange={(e) => handleSearch(e.target.value)}
                    bg="gray.700"
                    border="1px solid"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #3182CE" }}
                  />
                </InputGroup>

                <Select
                  placeholder="All Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  maxW="180px"
                  bg="gray.700"
                  border="1px solid"
                  borderColor="gray.600"
                  color="white"
                  _focus={{ borderColor: "blue.400" }}
                >
                  <option style={{backgroundColor: '#2D3748'}} value="active">Active</option>
                  <option style={{backgroundColor: '#2D3748'}} value="pending">Pending</option>
                  <option style={{backgroundColor: '#2D3748'}} value="planning">Planning</option>
                  <option style={{backgroundColor: '#2D3748'}} value="completed">Completed</option>
                  <option style={{backgroundColor: '#2D3748'}} value="cancelled">Cancelled</option>
                </Select>

                <Select
                  placeholder="All Types"
                  value={filters.projectType}
                  onChange={(e) => handleFilterChange("projectType", e.target.value)}
                  maxW="180px"
                  bg="gray.700"
                  border="1px solid"
                  borderColor="gray.600"
                  color="white"
                  _focus={{ borderColor: "blue.400" }}
                >
                  <option style={{backgroundColor: '#2D3748'}} value="construction">Construction</option>
                  <option style={{backgroundColor: '#2D3748'}} value="renovation">Renovation</option>
                  <option style={{backgroundColor: '#2D3748'}} value="maintenance">Maintenance</option>
                  <option style={{backgroundColor: '#2D3748'}} value="commercial">Commercial</option>
                  <option style={{backgroundColor: '#2D3748'}} value="residential">Residential</option>
                </Select>
              </HStack>
            </VStack>
          </CardBody>
        </Card> */}

        {/* Enhanced Error Alert */}
        {error && (
          <Alert status="error" borderRadius="lg" bg="red.900" borderColor="red.500">
            <AlertIcon color="red.400" />
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold" color="red.200">Error Loading Projects</Text>
              <Text fontSize="sm" color="red.300">{error}</Text>
              <Button 
                size="sm" 
                colorScheme="red" 
                variant="outline"
                onClick={fetchProjectsWithFilters}
                mt={2}
              >
                Retry
              </Button>
            </VStack>
          </Alert>
        )}

        {/* Projects Display */}
        {loading ? (
          <Card bg="gray.800">
            <CardBody>
              <Flex justify="center" p={16}>
                <VStack spacing={6}>
                  <Spinner size="xl" color="blue.400" thickness="4px" />
                  <VStack spacing={2}>
                    <Text color="white" fontSize="lg" fontWeight="semibold">
                      Loading Projects
                    </Text>
                    <Text color="gray.400" textAlign="center">
                      Please wait while we fetch your projects...
                    </Text>
                  </VStack>
                </VStack>
              </Flex>
            </CardBody>
          </Card>
        ) : projects.length === 0 ? (
          <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
            <CardBody>
              <VStack spacing={8} py={16}>
                <Icon as={FiFolder} boxSize={16} color="gray.600" />
                <VStack spacing={4}>
                  <Heading size="md" color="white" textAlign="center">
                    No Projects Found
                  </Heading>
                  <Text color="gray.400" textAlign="center" maxW="md">
                    {filters.search || filters.status || filters.projectType 
                      ? "No projects match your current filters. Try adjusting your search criteria."
                      : "You haven't created any projects yet. Get started by creating your first project."}
                  </Text>
                  {canCreateProjects() && !filters.search && !filters.status && !filters.projectType && (
                    <Button
                      leftIcon={<FiPlus />}
                      colorScheme="blue"
                      size="lg"
                      onClick={() => navigate("/dashboard/company/projects/new")}
                      mt={4}
                    >
                      Create Your First Project
                    </Button>
                  )}
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        ) : viewMode === 'grid' ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </SimpleGrid>
        ) : (
          // ✅ ENHANCED: Better table view with progress bars
          <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="gray.300" fontSize="sm" fontWeight="semibold">Project</Th>
                    {/* <Th color="gray.300" fontSize="sm" fontWeight="semibold">Status</Th> */}
                    <Th color="gray.300" fontSize="sm" fontWeight="semibold">Type</Th>
                    {/* <Th color="gray.300" fontSize="sm" fontWeight="semibold">Progress</Th> */}
                    <Th color="gray.300" fontSize="sm" fontWeight="semibold">Created</Th>
                    <Th color="gray.300" fontSize="sm" fontWeight="semibold">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {projects.map((project) => {
                    const progress = getProjectProgress(project);
                    return (
                      <Tr key={project.id} _hover={{ bg: "gray.700" }} transition="all 0.2s">
                        <Td py={4}>
                          <HStack spacing={3}>
                            <Avatar size="sm" name={project.name} bg="blue.500" />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="semibold" color="white" fontSize="sm">
                                {project.name}
                              </Text>
                              <Text fontSize="xs" color="gray.400" noOfLines={1}>
                                {project.description}
                              </Text>
                            </VStack>
                          </HStack>
                        </Td>
                        {/* <Td>
                          <Badge
                            colorScheme={getStatusColor(project.status)}
                            variant="solid"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            textTransform="capitalize"
                          >
                            {project.status}
                          </Badge>
                        </Td> */}
                        <Td>
                          <Text color="gray.300" fontSize="sm" textTransform="capitalize">
                            {project.projectType}
                          </Text>
                        </Td>
                        {/* <Td>
                          <VStack spacing={1} align="stretch" minW="120px">
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="gray.400">{progress}%</Text>
                            </HStack>
                            <Progress
                              value={progress}
                              colorScheme={getStatusColor(project.status)}
                              size="sm"
                              bg="gray.700"
                              borderRadius="full"
                            />
                          </VStack>
                        </Td> */}
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text color="gray.300" fontSize="sm">
                              {new Date(project.createdAt).toLocaleDateString()}
                            </Text>
                            <Text color="gray.500" fontSize="xs">
                              {new Date(project.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="View Project">
                              <IconButton
                                icon={<FiEye />}
                                size="sm"
                                variant="ghost"
                                color="blue.400"
                                _hover={{ bg: 'blue.900', color: 'blue.300' }}
                                onClick={() => navigate(`/dashboard/company/projects/${project.id}`)}
                                aria-label="View project"
                              />
                            </Tooltip>

                            {canEditProjects() && (
                              <Tooltip label="Edit Project">
                                <IconButton
                                  icon={<FiEdit3 />}
                                  size="sm"
                                  variant="ghost"
                                  color="yellow.400"
                                  _hover={{ bg: 'yellow.900', color: 'yellow.300' }}
                                  onClick={() => navigate(`/dashboard/company/projects/${project.id}/edit`)}
                                  aria-label="Edit project"
                                />
                              </Tooltip>
                            )}

                            {canDeleteProjects() && (
                              <Tooltip label="Delete Project">
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  variant="ghost"
                                  color="red.400"
                                  _hover={{ bg: 'red.900', color: 'red.300' }}
                                  onClick={() => handleDeleteProject(project.id)}
                                  aria-label="Delete project"
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default React.memo(ProjectManagement);
